import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Lang, Vars } from './types';
import { otherLang } from './types';
import { t as translate } from './t';
import type { DictionaryKey } from './dictionary';

export type LangState = {
  lang: Lang;
  /** An explicit choice (the toggle). It is remembered on this device and always wins. */
  setLang: (lang: Lang) => void;
  /**
   * The language to use when nobody has chosen one. Not remembered here: the
   * caller decides what the default is (the ops shell: English for owners and
   * managers, Spanish for the crew) and may change it as people sign in and out.
   */
  setDefaultLang: (lang: Lang) => void;
  toggle: () => void;
  /** The language the toggle would switch to. Handy for the button label. */
  other: Lang;
};

const LangContext = createContext<LangState | null>(null);

function readExplicit(storageKey: string): Lang | null {
  try {
    const stored = globalThis.localStorage?.getItem(storageKey);
    return stored === 'es' || stored === 'en' ? stored : null;
  } catch {
    return null;
  }
}

/** What is shown: a choice somebody made, else the default. */
export function resolveLang(explicit: Lang | null, fallback: Lang): Lang {
  return explicit ?? fallback;
}

export type LangProviderProps = {
  /** Guest shell passes 'en'. Ops shell passes 'es'. */
  defaultLang: Lang;
  /** Separate per shell so the two apps never fight over one key. */
  storageKey?: string;
  children: ReactNode;
};

export function LangProvider({ defaultLang, storageKey = 'dia.lang', children }: LangProviderProps) {
  const [explicit, setExplicit] = useState<Lang | null>(() => readExplicit(storageKey));
  const [fallback, setDefaultLang] = useState<Lang>(defaultLang);
  const lang = resolveLang(explicit, fallback);

  const setLang = useCallback(
    (next: Lang) => {
      setExplicit(next);
      try {
        globalThis.localStorage?.setItem(storageKey, next);
      } catch {
        // Private mode or a full disk. The toggle still works for this session.
      }
    },
    [storageKey],
  );

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo<LangState>(
    () => ({ lang, setLang, setDefaultLang, toggle: () => setLang(otherLang(lang)), other: otherLang(lang) }),
    [lang, setLang],
  );

  return createElement(LangContext.Provider, { value }, children);
}

export function useLang(): LangState {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useLang needs a LangProvider above it. Wrap the shell root.');
  return ctx;
}

export type Translator = LangState & {
  t: (key: DictionaryKey, vars?: Vars) => string;
  /** The same key in the other language, for a reveal-the-original tap. */
  tIn: (key: DictionaryKey, lang: Lang, vars?: Vars) => string;
};

/** The hook screens use: `const { t, lang, toggle } = useT();` */
export function useT(): Translator {
  const state = useLang();
  return useMemo<Translator>(
    () => ({
      ...state,
      t: (key, vars) => translate(key, state.lang, vars),
      tIn: (key, lang, vars) => translate(key, lang, vars),
    }),
    [state],
  );
}
